<template>
    <b-form validate @submit.prevent="validateForm">
        <b-row>
            <b-col md="6">
                <b-form-group
                    id="input-group-1"
                    label="Full name*"
                    label-for="input-1"
                    description="Full name of the recipient">
                    <b-form-input
                        id="input-1"
                        v-model="form.fullName"
                        type="text"
                        name="fullName"
                        :disabled="$attrs.disabled"
                        v-validate="'required'"
                        :class="{'is-valid': !errors.has('fullName') && form.fullName !== '', 'is-invalid': errors.has('fullName')}"
                        required/>
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group
                    label="Company"
                    label-for="input-company"
                    description="Company name for shipping">
                    <b-form-input
                        id="input-company"
                        v-model="form.company.name"
                        type="text"
                        name="company"
                        :disabled="$attrs.disabled"/>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col md="6">
                <b-form-group
                    id="input-group-phone"
                    label="Phone*"
                    label-for="input-phone"
                    :description="phoneCode ? 'Phone code of chosen country starts: ' + phoneCode : 'Phone for shipping address'">
                    <div>
                        <b-form-input
                            id="input-phone"
                            v-model="form.phone"
                            type="tel"
                            name="phone"
                            :disabled="$attrs.disabled"
                            v-validate="{required: true, max: 35 }"
                            :class="{'is-invalid': errors.has('phone'), 'is-valid': !errors.has('phone') && form.phone !== ''}"
                            required/>
                    </div>
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group
                    label="Company code"
                    label-for="input-company-code"
                    description="Company-code for shipping">
                    <b-form-input
                        id="input-company-code"
                        v-model="form.company.code"
                        type="text"
                        name="company-code"
                        :disabled="$attrs.disabled"/>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col md="6">
                <b-form-group
                    id="input-group-8"
                    label="Address*"
                    label-for="input-8"
                    description="Shipping street">
                    <b-form-input
                        id="input-8"
                        v-model="form.line1"
                        type="text"
                        name="line1"
                        data-vv-as="address"
                        :disabled="$attrs.disabled"
                        v-validate="'required|min:2'"
                        :class="{'is-valid': !errors.has('line1') && form.line1 !== '',
                            'is-invalid': errors.has('line1') }"
                        required/>
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group
                    label="Company VAT"
                    label-for="input-company-VAT"
                    description="Company VAT no.">
                    <b-form-input
                        id="input-company-VAT"
                        v-model="form.company.VAT"
                        type="text"
                        name="companyVAT"
                        data-vv-as="company VAT"
                        v-validate="'min:2'"
                        :class="{'is-valid': !errors.has('companyVAT') && form.company.VAT !== null && form.company.VAT !== '',
                            'is-invalid': errors.has('companyVAT') }"
                        :disabled="$attrs.disabled"/>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col md="6">
                <b-form-group
                    id="input-group-7"
                    label="City*"
                    label-for="input-7"
                    description="Shipping city">
                    <b-form-input
                        id="input-7"
                        v-model="form.city"
                        type="text"
                        name="city"
                        :disabled="$attrs.disabled"
                        v-validate="'required|min:2'"
                        :class="{'is-valid': getLength(form.city) >= 2 && form.city !== '',
                            'is-invalid': errors.has('city') }"
                        required/>
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group
                    id="input-group-6"
                    label="Postal code*"
                    label-for="input-6"
                    :description="postalCodePreview ? 'Shipping postal code with format ' + postalCodePreview : 'Shipping postal code'">
                    <b-form-input
                        id="input-6"
                        v-model="form.postalCode"
                        type="text"
                        name="postalCode"
                        :disabled="$attrs.disabled"
                        :data-vv-as="`${postalCodePreview}`"
                        v-validate="{required: !!postalCodeRequired, regex: postalCodeFormat}"
                        :class="{'is-invalid': errors.has('postalCode'), 'is-valid': !errors.has('postalCode') && form.postalCode !== ''}" />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col md="6">
                <b-form-group
                    id="input-group-3"
                    label="Country*"
                    label-for="input-3">
                    <v-select
                        class="v-select-user"
                        v-model="form.country"
                        @input="changeCountry"
                        name="countries"
                        label="nameEn"
                        :options="countries"
                        :clearable="false"
                        v-validate="'required'"
                        placeholder="Choose country"
                        :class="{'is-invalid': errors.has('countries')}"
                        :disabled="$attrs.disabled"
                        @search="searchCountry">
                    </v-select>
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group
                    id="input-group-5"
                    label="Region"
                    label-for="input-5">
                    <b-form-input
                        id="input-5"
                        v-model="form.state"
                        type="text"
                        name="state"
                        v-validate="'min:2'"
                        :class="{'is-valid': !errors.has('state') && form.state !== '' && form.state !== null,
                            'is-invalid': errors.has('state')}"
                        :disabled="$attrs.disabled"/>
                </b-form-group>
            </b-col>
        </b-row>
        <template v-for="name in Object.keys($slots)">
            <slot :name="name"/>
        </template>
        <b-modal
            id="modal-multi-error"
            size="md"
            hide-footer
            title="Error">
            <b-alert
                v-for="(error, index) in $validator.errors.all()"
                :key="index"
                variant="danger"
                show
            >
                {{error}}
            </b-alert>
            <template v-slot:modal-footer="{ cancel }" class="text-right">
                <div class="text-right pt-3">
                    <b-button variant="secondary" @click="closeErrorModal">
                        Cancel
                    </b-button>
                </div>
            </template>
        </b-modal>
    </b-form>
</template>

<script>
    // import postalCodes from 'postal-codes-js'
    import validators from './validators'
    export default {
        name: 'AddressForm',
        props: {
            address: {
                type: Object,
                required: false,
            },
            submittedForm: {
                type: Function,
                required: true
            }
        },
        data() {
            return {
                countries: [],
                phoneCode: '',
                postalCodePreview: '',
                postalCodeFormat: /(?:)/,
                postalCodeRequired: true,
                // postalCodeError: null,
                GEO: {},
                form: {
                    fullName: '',
                    company: {
                        name: null,
                        code: null,
                        VAT: null
                    },
                    phone: '',
                    country: null,
                    countryCode: '',
                    state: '',
                    city: '',
                    postalCode: '',
                    line1: ''
                    // line2: '',
                }
            }
        },
        watch: {
            address: {
                handler: function(newVal) {
                    if (newVal) {
                        // deep copy of object
                        this.form = JSON.parse(JSON.stringify(newVal))
                        this.changeCountry(newVal.country)
                        // this.$validator.reset()
                    }
                },
                deep: true,
                immediate: true
            }
        },
        created() {
            validators()
        },
        mounted() {
           this.getCountries()
           if (this.address) {
               this.$validator.validateAll()
           }
        },
        methods: {
            getCountries(loading = null, search = '') {
                let params = {
                    page: '1',
                    perPage: '250',
                    search: search
                }
                this.$api('GET', this.$endpoints.countries, null, params).then(response => {
                    this.countries = response.items.sort((a, b) => a.nameEn.localeCompare(b.nameEn))
                    if (loading) loading(false)
                })
            },
            searchCountry(search, loading) {
                loading(true)
                this.getCountries(loading, search)
            },
            changeCountry(data) {
                if (data) {
                    this.form.countryCode = data.code
                    this.phoneCode = '+' + data.phoneCode
                    this.postalCodePreview = data.postalCodeFormatPreview
                    this.postalCodeFormat = data.postalCodeFormat ? new RegExp('^' + data.postalCodeFormat) : /(?:)/
                    this.postalCodeRequired = data.postCodeRequired
                }
            },
            validateForm() {
                this.$validator.validateAll()
                    .then((result) => {
                        // if (result && this.validatePostalCode()) {
                        if (result) {
                            let data = {...this.form}
                            data.phone = this.form.phone
                            this.submittedForm(data)
                            this.$root.$emit('bv::hide::modal', 'addressesModal')
                        } else {
                            this.showErrorModal()
                        }
                    })
            },
            /* validatePostalCode() {
                let postalCode = postalCodes.validate(this.form.countryCode, this.form.postalCode)
                this.postalCodeError = typeof postalCode === 'string' ? postalCode + '<br>Code postal format: ' + this.postalCodePreview : null
                return postalCode && typeof postalCode !== 'string'
            }, */
            getLength(value) {
                return value ? value.length : 0
            },
            showErrorModal() {
                this.$bvModal.show('modal-multi-error')
            },
            closeErrorModal() {
                this.$bvModal.hide('modal-multi-error')
                // this.$validator.reset()
            }
        }
    }
</script>

<style lang="scss">
    .with-mask {
        display: flex;
        align-items: center;
        .phone-code {
            color: gray;
        }
        .form-control {
            padding-left: 5px;
            margin-left: 3px;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/UserPanel/Forms/AddressForm.vue